import { Add as AddIcon, DeleteOutline as DeleteIcon, DownloadOutlined as DownloadIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDocuments } from "../../../hooks/UseDocuments";
import type { DocumentItem } from "../../../types/documents";
import AddDocumentModal from "./AddDocumentModal";

type DocumentsSectionProps = {
  documents: DocumentItem[];
  bookingId: string;
  onAddDocument?: (newDoc: DocumentItem) => void;
  loading?: boolean;
  onViewInvoice?: () => void;
  canViewInvoice?: boolean;
  canAddDocument?: boolean;
  canDeleteDocument?: boolean;
};

const DocumentsSection = ({
  documents,
  bookingId,
  onAddDocument,
  loading = false,
  onViewInvoice,
  canViewInvoice = false,
  canAddDocument = false,
  canDeleteDocument = false,
}: DocumentsSectionProps) => {
  const [localDocuments, setLocalDocuments] = useState<DocumentItem[]>(documents);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { documentLoading, handleViewDocument, handleSaveDocument, handleDocumentDelete } = useDocuments();

  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);

  const handleSave = async (payload: {
    name: string;
    description: string;
    category: string;
    file: File;
  }) => {
    if (!bookingId) return;

    const created = await handleSaveDocument(
      bookingId,
      payload.name,
      payload.category,
      payload.description,
      payload.file,
    );

    if (created) {
      setLocalDocuments((prev) => [created, ...prev]);
      onAddDocument?.(created);
    }
  };

  const handleDelete = async (id: string) => {
    const success = await handleDocumentDelete(id);

    if (success) {
      setLocalDocuments((prev) => prev.filter((doc) => String(doc.id) !== String(id)));
    }
  };

  return (
    <>
      <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 0, border: "1px solid #f0f4f8" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
            Documents
          </Typography>

          <Stack direction="row" spacing={1}>
            {canViewInvoice && onViewInvoice && (
              <Button variant="outlined" size="small" onClick={onViewInvoice}>
                View Invoice
              </Button>
            )}

            {canAddDocument && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsModalOpen(true)}
                sx={{ bgcolor: "#724e41", color: "white", px: 1.5, borderRadius: 2, textTransform: "none" }}
              >
                Add
              </Button>
            )}
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : localDocuments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {localDocuments.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  border: "1px solid #edf2f7",
                  borderRadius: 2,
                  p: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {doc.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {doc.description || "No description provided"}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => handleViewDocument(doc.document_field)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {canDeleteDocument && (
                    <IconButton size="small" onClick={() => handleDelete(String(doc.id))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <AddDocumentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        loading={documentLoading}
        categories={["General", "Invoice", "Service", "Other"]}
        onSave={handleSave}
      />
    </>
  );
};

export default DocumentsSection;
   